import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import PerformanceSummaryCards from "../components/performance/PerformanceSummaryCards";
import PerformanceIncidents from "../components/performance/PerformanceIncidents";
import PerformanceStatusBreakdownTable from "../components/performance/PerformanceStatusBreakdownTable";
import PerformanceDetailDrawer from "../components/performance/PerformanceDetailDrawer";
import PerformanceBreakdownTable, {
  BreakdownColumn,
} from "../components/performance/PerformanceBreakdownTable";
import type {
  AdminPerformanceResponse,
  PerformanceClientRow,
  PerformanceDriverRow,
  PerformancePeriodKey,
  PerformanceZoneRow,
  ShippingType,
  StatusDetail,
  StatusDetailGroup,
  SlaDetail,
  SlaDetailGroup,
} from "../types/performance";

type BreakdownTab = "driver" | "client" | "zone";

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const periodPillsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const pillStyle = (active: boolean): React.CSSProperties => ({
  minHeight: "40px",
  padding: "0 18px",
  borderRadius: "999px",
  border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
  background: active ? "var(--primary-soft)" : "transparent",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  fontSize: "14px",
  fontWeight: active ? 700 : 600,
  cursor: "pointer",
});

const periodLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const customRangeRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  alignItems: "center",
  flexWrap: "wrap",
};

const dateInputStyle: React.CSSProperties = {
  minHeight: "40px",
  padding: "0 12px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const customRangeErrorStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--danger)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionSubtextStyle: React.CSSProperties = {
  margin: "6px 0 16px 0",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const statTileGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px",
};

const statTileStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "16px",
};

const statTileLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "8px",
};

const statTileValueStyle: React.CSSProperties = {
  fontSize: "22px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const tabsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  marginBottom: "16px",
};

const tabButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: "0 16px",
  minHeight: "38px",
  borderRadius: "12px",
  border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
  background: active ? "var(--primary-soft)" : "transparent",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
  cursor: "pointer",
});

const footnoteStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  lineHeight: 1.6,
};

const stateBoxStyle: React.CSSProperties = {
  padding: "20px",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`;

const PERIOD_OPTIONS: { key: PerformancePeriodKey; label: string }[] = [
  { key: "previous_day", label: "Día anterior" },
  { key: "current_week", label: "Semana actual" },
  { key: "previous_week", label: "Semana anterior" },
];

const SHIPPING_TYPE_OPTIONS: { key: ShippingType; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "flex", label: "Flex" },
  { key: "turbo", label: "Turbo" },
  { key: "non_flex", label: "No Flex" },
];

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const STATUS_DETAIL_LIMIT = 50;

type CustomRange = { from: string; to: string };

// Query compartida por el fetch principal y el drill-down: ambos deben
// consultar exactamente el mismo período/shippingType, si no el detalle
// mostraría filas de otro recorte distinto al que se ve en pantalla.
function buildBaseQuery(period: PerformancePeriodKey, customRange: CustomRange | null, shippingType: ShippingType) {
  const periodQuery =
    period === "custom" && customRange
      ? `period=custom&from=${customRange.from}&to=${customRange.to}`
      : `period=${period}`;
  return `${periodQuery}&shippingType=${shippingType}`;
}

const Performance = () => {
  const { token } = useAuth();

  const [period, setPeriod] = useState<PerformancePeriodKey>("current_week");
  const [shippingType, setShippingType] = useState<ShippingType>("all");
  const [customFromInput, setCustomFromInput] = useState<string>("");
  const [customToInput, setCustomToInput] = useState<string>("");
  const [appliedCustomRange, setAppliedCustomRange] = useState<CustomRange | null>(null);
  const [customRangeError, setCustomRangeError] = useState<string>("");
  const [data, setData] = useState<AdminPerformanceResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>("driver");

  // Drill-down: qué categoría está abierta, su label legible, y el detalle
  // paginado que devuelve el backend para esa categoría. Dos "sabores" sobre
  // el mismo mecanismo: `status` (operationalStatus, tabla "Desglose del
  // período") y `sla` (SLA exacto, cards de esta iteración) — comparten el
  // mismo drawer, pero pegan a query params distintos del backend.
  type DetailKind = "status" | "sla";
  const [selectedDetail, setSelectedDetail] = useState<{ kind: DetailKind; group: StatusDetailGroup | SlaDetailGroup; label: string } | null>(null);
  const [detailOffset, setDetailOffset] = useState<number>(0);
  const [detailData, setDetailData] = useState<StatusDetail | SlaDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string>("");

  // "Rango personalizado" solo dispara el request cuando el usuario aplica
  // un rango válido (botón "Aplicar") — nunca mientras tipea cada dígito de
  // las fechas, porque el efecto depende de `appliedCustomRange` (el rango
  // confirmado), no de los inputs en crudo.
  useEffect(() => {
    if (period === "custom" && !appliedCustomRange) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPerformance = async () => {
      setLoading(true);
      setError("");
      try {
        const query = buildBaseQuery(period, appliedCustomRange, shippingType);
        const res = await authFetch(`/v1/performance/admin?${query}`, { token });
        const payload: AdminPerformanceResponse = await res.json().catch(() => ({} as AdminPerformanceResponse));

        if (!res.ok || payload?.ok === false) {
          throw new Error(payload?.error || "Error al cargar Performance");
        }

        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) {
          console.error("Error cargando Performance:", err);
          setError(err instanceof Error ? err.message : "Error al cargar Performance");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPerformance();
    return () => {
      cancelled = true;
    };
  }, [token, period, appliedCustomRange, shippingType]);

  // Si cambia período/rango/tipo de envío, el detalle abierto quedaría
  // mostrando filas de otro recorte — se cierra en vez de dejarlo desalineado.
  useEffect(() => {
    setSelectedDetail(null);
    setDetailData(null);
    setDetailError("");
    setDetailOffset(0);
  }, [period, appliedCustomRange, shippingType]);

  // Drill-down: mismo período/shippingType que el dashboard, + statusDetail
  // o slaDetail paginado según cuál se haya seleccionado. No repite el fetch
  // principal ni cachea nada del lado del cliente — el backend es la fuente
  // de verdad.
  useEffect(() => {
    if (!selectedDetail) return;
    if (period === "custom" && !appliedCustomRange) return;

    let cancelled = false;

    const fetchDetail = async () => {
      setDetailLoading(true);
      setDetailError("");
      try {
        const base = buildBaseQuery(period, appliedCustomRange, shippingType);
        const paramPrefix = selectedDetail.kind === "sla" ? "slaDetail" : "statusDetail";
        const query = `${base}&${paramPrefix}=${selectedDetail.group}&${paramPrefix}Limit=${STATUS_DETAIL_LIMIT}&${paramPrefix}Offset=${detailOffset}`;
        const res = await authFetch(`/v1/performance/admin?${query}`, { token });
        const payload: AdminPerformanceResponse = await res.json().catch(() => ({} as AdminPerformanceResponse));

        if (!res.ok || payload?.ok === false) {
          throw new Error(payload?.error || "Error al cargar el detalle");
        }

        if (!cancelled) setDetailData((selectedDetail.kind === "sla" ? payload.slaDetail : payload.statusDetail) ?? null);
      } catch (err) {
        if (!cancelled) {
          console.error("Error cargando detalle de Performance:", err);
          setDetailError(err instanceof Error ? err.message : "Error al cargar el detalle");
          setDetailData(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [token, period, appliedCustomRange, shippingType, selectedDetail, detailOffset]);

  const handleSelectStatusGroup = (group: StatusDetailGroup, label: string) => {
    setSelectedDetail({ kind: "status", group, label });
    setDetailOffset(0);
  };

  const handleSelectSlaGroup = (group: SlaDetailGroup, label: string) => {
    setSelectedDetail({ kind: "sla", group, label });
    setDetailOffset(0);
  };

  const handleCloseDrawer = () => {
    setSelectedDetail(null);
    setDetailData(null);
    setDetailError("");
  };

  const handleApplyCustomRange = () => {
    if (!DATE_KEY_RE.test(customFromInput) || !DATE_KEY_RE.test(customToInput)) {
      setCustomRangeError("Completá ambas fechas.");
      return;
    }
    if (customFromInput > customToInput) {
      setCustomRangeError("'Desde' no puede ser posterior a 'Hasta'.");
      return;
    }
    setCustomRangeError("");
    setAppliedCustomRange({ from: customFromInput, to: customToInput });
  };

  const driverColumns: BreakdownColumn<PerformanceDriverRow>[] = useMemo(
    () => [
      { key: "cadete", label: "Driver", render: (r) => r.cadete, sortValue: (r) => r.cadete },
      { key: "deliveries", label: "Envíos", align: "right", render: (r) => r.deliveries, sortValue: (r) => r.deliveries },
      { key: "delivered", label: "Entregados", align: "right", render: (r) => r.delivered, sortValue: (r) => r.delivered },
      { key: "before21", label: "Antes 21", align: "right", render: (r) => r.before21, sortValue: (r) => r.before21 },
      { key: "after21", label: "Después 21", align: "right", render: (r) => r.after21, sortValue: (r) => r.after21 },
      {
        key: "before21Rate",
        label: "% antes 21",
        align: "right",
        render: (r) => formatPercent(r.before21Rate),
        sortValue: (r) => r.before21Rate,
      },
      { key: "incidents", label: "Incidencias", align: "right", render: (r) => r.incidents, sortValue: (r) => r.incidents },
      { key: "delayed", label: "Demorados", align: "right", render: (r) => r.delayed, sortValue: (r) => r.delayed },
    ],
    []
  );

  const clientColumns: BreakdownColumn<PerformanceClientRow>[] = useMemo(
    () => [
      { key: "client", label: "Cliente / Store", render: (r) => r.client, sortValue: (r) => r.client },
      { key: "deliveries", label: "Envíos", align: "right", render: (r) => r.deliveries, sortValue: (r) => r.deliveries },
      { key: "delivered", label: "Entregados", align: "right", render: (r) => r.delivered, sortValue: (r) => r.delivered },
      {
        key: "deliveredRate",
        label: "% entregado",
        align: "right",
        render: (r) => formatPercent(r.deliveredRate),
        sortValue: (r) => r.deliveredRate,
      },
      { key: "before21", label: "Antes 21", align: "right", render: (r) => r.before21, sortValue: (r) => r.before21 },
      {
        key: "before21Rate",
        label: "% antes 21",
        align: "right",
        render: (r) => formatPercent(r.before21Rate),
        sortValue: (r) => r.before21Rate,
      },
      { key: "incidents", label: "Incidencias", align: "right", render: (r) => r.incidents, sortValue: (r) => r.incidents },
    ],
    []
  );

  const zoneColumns: BreakdownColumn<PerformanceZoneRow>[] = useMemo(
    () => [
      { key: "zone", label: "Zona", render: (r) => r.zone, sortValue: (r) => r.zone },
      { key: "deliveries", label: "Envíos", align: "right", render: (r) => r.deliveries, sortValue: (r) => r.deliveries },
      { key: "delivered", label: "Entregados", align: "right", render: (r) => r.delivered, sortValue: (r) => r.delivered },
      {
        key: "deliveredRate",
        label: "% entregado",
        align: "right",
        render: (r) => formatPercent(r.deliveredRate),
        sortValue: (r) => r.deliveredRate,
      },
      {
        key: "before21Rate",
        label: "% antes 21",
        align: "right",
        render: (r) => formatPercent(r.before21Rate),
        sortValue: (r) => r.before21Rate,
      },
      { key: "incidents", label: "Incidencias", align: "right", render: (r) => r.incidents, sortValue: (r) => r.incidents },
    ],
    []
  );

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Performance</h1>
          <p style={subtitleStyle}>
            Desempeño operativo sobre datos legacy (Lightdata): entregas, incidencias y SLA por driver, cliente y zona.
          </p>
        </div>
      </div>

      <div>
        <div style={{ ...periodLabelStyle, marginBottom: "8px" }}>Período</div>
        <div style={periodPillsRowStyle}>
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              style={pillStyle(period === option.key)}
              onClick={() => setPeriod(option.key)}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            style={pillStyle(period === "custom")}
            onClick={() => setPeriod("custom")}
          >
            Rango personalizado
          </button>
          {data?.period ? <span style={periodLabelStyle}>Período: {data.period.label}</span> : null}
        </div>
      </div>

      <div>
        <div style={{ ...periodLabelStyle, marginBottom: "8px" }}>Tipo de envío</div>
        <div style={periodPillsRowStyle}>
          {SHIPPING_TYPE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              style={pillStyle(shippingType === option.key)}
              onClick={() => setShippingType(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div style={customRangeRowStyle}>
          <span style={periodLabelStyle}>Desde</span>
          <input
            type="date"
            value={customFromInput}
            onChange={(e) => setCustomFromInput(e.target.value)}
            style={dateInputStyle}
          />
          <span style={periodLabelStyle}>Hasta</span>
          <input
            type="date"
            value={customToInput}
            onChange={(e) => setCustomToInput(e.target.value)}
            style={dateInputStyle}
          />
          <Button onClick={handleApplyCustomRange} disabled={!customFromInput || !customToInput}>
            Aplicar
          </Button>
          {customRangeError ? <span style={customRangeErrorStyle}>{customRangeError}</span> : null}
        </div>
      )}

      {period === "custom" && !appliedCustomRange ? (
        <Card>
          <div style={stateBoxStyle}>Elegí un rango de fechas y presioná "Aplicar" para calcular Performance.</div>
        </Card>
      ) : loading ? (
        <Card>
          <div style={stateBoxStyle}>Cargando Performance...</div>
        </Card>
      ) : error ? (
        <Card>
          <div style={{ ...stateBoxStyle, color: "var(--danger)" }}>{error}</div>
        </Card>
      ) : !data ? null : (
        <>
          {data.sla.excludedCurrentDay.count > 0 && (
            <Card>
              <div style={{ ...footnoteStyle, fontSize: "13px" }}>
                <strong>Día actual pendiente de cierre:</strong> {data.sla.excludedCurrentDay.count} envío(s) del {data.sla.excludedCurrentDay.date} quedan fuera del SLA de este período — Performance solo evalúa días cerrados (23:00 hs en adelante ya sincronizado). El seguimiento en vivo del día de hoy se resuelve en Torre de Control.
              </div>
            </Card>
          )}

          <div>
            <h2 style={sectionTitleStyle}>Resumen de desempeño</h2>
            <p style={sectionSubtextStyle}>SLA general: solo días cerrados. Click en una card con datos para ver el detalle.</p>
            <PerformanceSummaryCards summary={data.summary} sla={data.sla} elite={data.elite} timing={data.timing} onSelectSlaGroup={handleSelectSlaGroup} />
          </div>

          <PerformanceIncidents
            absent={data.absent}
            rescheduled={data.rescheduled}
            excluded={data.excluded}
            pending={data.pending}
            onSelectSlaGroup={handleSelectSlaGroup}
          />

          <PerformanceStatusBreakdownTable statusBreakdown={data.statusBreakdown} onSelectGroup={handleSelectStatusGroup} />

          {selectedDetail && (
            <PerformanceDetailDrawer
              label={selectedDetail.label}
              detail={detailData}
              loading={detailLoading}
              error={detailError}
              onClose={handleCloseDrawer}
              onPageChange={setDetailOffset}
            />
          )}


          <Card>
            <h2 style={sectionTitleStyle}>Detalle de operación</h2>
            <p style={sectionSubtextStyle}>Desglose por driver, cliente/store o zona.</p>

            <div style={tabsRowStyle}>
              <button type="button" style={tabButtonStyle(breakdownTab === "driver")} onClick={() => setBreakdownTab("driver")}>
                Driver
              </button>
              <button type="button" style={tabButtonStyle(breakdownTab === "client")} onClick={() => setBreakdownTab("client")}>
                Cliente / Store
              </button>
              <button type="button" style={tabButtonStyle(breakdownTab === "zone")} onClick={() => setBreakdownTab("zone")}>
                Zona
              </button>
            </div>

            {breakdownTab === "driver" && (
              <PerformanceBreakdownTable
                columns={driverColumns}
                rows={data.drivers}
                getRowKey={(row, index) => `${row.cadete}-${index}`}
                getSearchText={(row) => row.cadete}
                searchPlaceholder="Buscar driver..."
                emptyLabel="No hay drivers con envíos en este período."
              />
            )}

            {breakdownTab === "client" && (
              <PerformanceBreakdownTable
                columns={clientColumns}
                rows={data.clients}
                getRowKey={(row, index) => `${row.client}-${index}`}
                getSearchText={(row) => row.client}
                searchPlaceholder="Buscar cliente / store..."
                emptyLabel="No hay clientes con envíos en este período."
              />
            )}

            {breakdownTab === "zone" && (
              <PerformanceBreakdownTable
                columns={zoneColumns}
                rows={data.zones}
                getRowKey={(row, index) => `${row.zone}-${index}`}
                getSearchText={(row) => row.zone}
                searchPlaceholder="Buscar zona..."
                emptyLabel="No hay zonas con envíos en este período."
              />
            )}
          </Card>

          <Card>
            <h2 style={sectionTitleStyle}>Distribución por universo de envío</h2>
            <p style={sectionSubtextStyle}>
              Composición real del período completo (independiente del filtro "Tipo de envío" elegido arriba) — sirve para verificar que la clasificación cubre todo el dataset.
            </p>
            <div style={statTileGridStyle} className="performance-stat-grid">
              <div style={statTileStyle}>
                <div style={statTileLabelStyle}>Flex</div>
                <div style={statTileValueStyle}>{data.shippingDistribution.flexNormal}</div>
              </div>
              <div style={statTileStyle}>
                <div style={statTileLabelStyle}>Turbo</div>
                <div style={statTileValueStyle}>{data.shippingDistribution.flexTurbo}</div>
              </div>
              <div style={statTileStyle}>
                <div style={statTileLabelStyle}>No Flex</div>
                <div style={statTileValueStyle}>{data.shippingDistribution.nonFlex}</div>
              </div>
              <div style={statTileStyle}>
                <div style={statTileLabelStyle}>Sin clasificar (Unknown)</div>
                <div style={statTileValueStyle}>{data.shippingDistribution.unknown}</div>
              </div>
            </div>
            <div style={{ ...footnoteStyle, marginTop: "12px" }}>
              Total del período: {data.shippingDistribution.total} envío(s).
            </div>
            <style>
              {`@media (max-width: 1180px) { .performance-stat-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } } @media (max-width: 720px) { .performance-stat-grid { grid-template-columns: 1fr !important; } }`}
            </style>
          </Card>

          {(data.dataQuality.unparseableDatesInWindow > 0 ||
            data.dataQuality.unknownShippingUniverse > 0 ||
            data.dataQuality.unknownStatusCount > 0) && (
            <Card>
              <h2 style={sectionTitleStyle}>Calidad de datos del período</h2>
              <div style={{ ...footnoteStyle, marginTop: "10px" }}>
                {data.dataQuality.unparseableDatesInWindow > 0 && (
                  <div>
                    {data.dataQuality.unparseableDatesInWindow} envío(s) con <code>fecha_estado</code> en formato no reconocido — excluidos del período.
                  </div>
                )}
                {data.dataQuality.unknownShippingUniverse > 0 && (
                  <div style={{ marginTop: "6px" }}>
                    {data.dataQuality.unknownShippingUniverse} envío(s) con <code>origen</code> no reconocido (ni Flex, ni Turbo, ni No Flex) — no forzados a "No Flex", visibles solo en "Todos".
                    {data.dataQuality.unknownShippingUniverseExamples.length > 0 && (
                      <div style={{ marginTop: "4px" }}>
                        Ejemplos: {data.dataQuality.unknownShippingUniverseExamples
                          .map((ex) => `${ex.numero_tracking ?? "?"} (origen="${ex.origen ?? ""}")`)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                )}
                {data.dataQuality.unknownStatusCount > 0 && (
                  <div style={{ marginTop: "6px" }}>
                    {data.dataQuality.unknownStatusCount} envío(s) con <code>estado</code> que el sistema no sabe interpretar (ni entregado, ni en curso, ni ninguna incidencia conocida) — nunca se cuentan como "en curso" por defecto.
                    {data.dataQuality.unknownStatusExamples.length > 0 && (
                      <div style={{ marginTop: "4px" }}>
                        Ejemplos: {data.dataQuality.unknownStatusExamples
                          .map((ex) => `${ex.numero_tracking ?? "?"} (estado="${ex.estado ?? ""}")`)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Performance;
